/**
 * Etapa 2 - Marca: logo (com preview local) e tom de cor.
 *
 * O arquivo e lido com `FileReader` e guardado como data URL no proprio
 * formulario: nada sobe para lugar nenhum na previa, e o preview e exatamente
 * o que o cabecalho vai mostrar (mesmo `CompanyLogo`). O limite de 1 MB
 * existe porque a data URL vive em memoria e na revisao; um PNG de 8 MB
 * travaria a tela sem avisar.
 */

import { useId, useState } from 'react';
import { Badge, Button, CompanyLogo, Fieldset, Icon, Panel } from '../../../../components/ui';
import { iniciaisDe } from '../../identidade';
import { SeletorDeTom } from '../SeletorDeTom';
import type { EtapaProps } from './props';

const TAMANHO_MAX_BYTES = 1_048_576;
const TIPOS_ACEITOS = 'image/png,image/jpeg,image/webp,image/svg+xml';

export function EtapaMarca({ form, onChange }: EtapaProps): JSX.Element {
  const inputId = useId();
  const [erroArquivo, setErroArquivo] = useState<string | null>(null);

  const nome = form.nome.trim() === '' ? 'Nova empresa' : form.nome.trim();
  const preview = { name: nome, logoInitials: iniciaisDe(nome), logoTone: form.logoTone, logoUrl: form.logoDataUrl };

  const aoEscolher = (arquivo: File | undefined): void => {
    if (arquivo === undefined) {
      return;
    }
    if (arquivo.size > TAMANHO_MAX_BYTES) {
      setErroArquivo('A imagem passa de 1 MB. Reduza o arquivo e tente de novo.');
      return;
    }
    const leitor = new FileReader();
    leitor.onload = () => {
      if (typeof leitor.result === 'string') {
        onChange({ logoDataUrl: leitor.result, logoNomeArquivo: arquivo.name });
        setErroArquivo(null);
      }
    };
    leitor.onerror = () => setErroArquivo('Não foi possível ler a imagem.');
    leitor.readAsDataURL(arquivo);
  };

  return (
    <div className="grid gap-4 lg:grid-cols-3">
      <Fieldset legend="Logo" description="PNG, JPG, WebP ou SVG até 1 MB. Sem imagem, o painel usa as iniciais na cor escolhida.">
        <div className="flex flex-col gap-3">
          <div className="flex flex-wrap items-center gap-3">
            {/* O input fica escondido (sr-only) e o label faz as vezes de botao: o anel de foco vai no label via `peer`, senao quem navega por teclado nao ve onde esta. */}
            <input id={inputId} type="file" accept={TIPOS_ACEITOS} className="peer sr-only" onChange={(e) => aoEscolher(e.target.files?.[0])} />
            <label
              htmlFor={inputId}
              className="inline-flex h-9 cursor-pointer items-center gap-2 whitespace-nowrap rounded-lg border border-line bg-surface px-4 text-sm font-medium text-ink shadow-soft transition-colors hover:border-brand/60 hover:text-brand peer-focus-visible:outline peer-focus-visible:outline-2 peer-focus-visible:outline-offset-2 peer-focus-visible:outline-brand"
            >
              <Icon name="upload" size="sm" />
              {form.logoDataUrl === null ? 'Escolher imagem' : 'Trocar imagem'}
            </label>
            {form.logoDataUrl === null ? null : (
              <Button variant="ghost" icon="lixeira" onClick={() => onChange({ logoDataUrl: null, logoNomeArquivo: null })}>
                Remover
              </Button>
            )}
          </div>
          <p role="status" className="text-xs text-ink-subtle">
            {erroArquivo !== null ? <span className="text-status-critical">{erroArquivo}</span> : form.logoNomeArquivo === null ? 'Nenhuma imagem escolhida.' : `Arquivo: ${form.logoNomeArquivo} (fica só neste navegador na prévia).`}
          </p>
        </div>
        <SeletorDeTom value={form.logoTone} onChange={(tone) => onChange({ logoTone: tone })} />
      </Fieldset>

      <Panel className="flex flex-col gap-4 p-5 lg:col-span-2">
        <h3 className="text-sm font-semibold text-ink">Como vai aparecer</h3>
        <div className="flex items-center gap-4 rounded-xl border border-line bg-elevated px-4 py-3">
          <CompanyLogo company={preview} size="xl" />
          <div className="min-w-0">
            <p className="truncate text-lg font-semibold text-ink">{nome}</p>
            <p className="truncate text-sm text-ink-muted">{form.segmento.trim() === '' ? 'Segmento (etapa 1)' : form.segmento}</p>
          </div>
          <Badge tone="warning" dot className="ml-auto">
            Em implantação
          </Badge>
        </div>
        <div className="flex items-center gap-3 rounded-xl border border-line bg-elevated px-4 py-3">
          <CompanyLogo company={preview} size="sm" />
          <span className="text-sm text-ink">{nome}</span>
          <span className="ml-auto text-xs text-ink-subtle">no cabeçalho do painel</span>
        </div>
        <p className="text-xs text-ink-subtle">O logo e o nome ficam em cima em todas as telas do CRM da empresa, para nunca haver dúvida de qual painel está aberto.</p>
      </Panel>
    </div>
  );
}
